/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag Fragment */
import type { BoxProps } from '@mantine/core';
import { Box, Popover, UnstyledButton } from '@mantine/core';
import type { ThemeUIStyleObject } from '@theme-ui/core';
import type { CSSProperties, ReactElement } from 'react';
import { useState } from 'react';
import { KeepScale } from 'react-zoom-pan-pinch';
import { jsx } from 'theme-ui';
import { useScale } from './_FloorPlan.useScale';

export default function FloorPlanHotspot({
  primaryColor,
  contrastColor,
  isHighlighted,
  id,
  position,
  popover,
  tooltip,
  isFavorite,
  style,
  sx,
  ...rest
}: BoxProps & {
  primaryColor: string | undefined;
  contrastColor: string | undefined;
  isHighlighted?: boolean;
  id: string;
  popover: ReactElement;
  tooltip: ReactElement;
  position: Pick<CSSProperties, 'left' | 'top' | 'width' | 'height'>;
  isFavorite?: boolean;
  withinPortal?: boolean;
  sx?: ThemeUIStyleObject;
}) {
  const [isOpened, setOpened] = useState(false);
  const [isHovered, setHovered] = useState(false);
  const isPrimary = isHighlighted || isHovered || isOpened;
  const bg = isPrimary ? primaryColor : 'gray.6';
  const c = isPrimary ? contrastColor : 'white';
  const scale = useScale();
  return (
    <Popover
      withinPortal={isOpened}
      withArrow
      arrowSize={isOpened ? undefined : 12}
      offset={0}
      position="top"
      positionDependencies={[scale]}
      opened={isPrimary || isFavorite}
      onChange={setOpened}
    >
      <Popover.Target>
        <Box
          sx={{ transform: 'translate(-50%, -50%)', ...sx }}
          pos="absolute"
          style={{ ...position, ...style }}
          {...rest}
        >
          <UnstyledButton
            id={id}
            onClick={() => setOpened(!isOpened)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            pos="absolute"
            inset={0}
            ml="50%"
            mt="50%"
            miw={20}
            mih={20}
            sx={{ transform: 'translate(-50%, -50%)' }}
          />
        </Box>
      </Popover.Target>
      <KeepScale>
        <Popover.Dropdown p={4} bg={bg} c={c}>
          <div>
            {isOpened ? popover : isPrimary || isFavorite ? tooltip : null}
          </div>
        </Popover.Dropdown>
      </KeepScale>
    </Popover>
  );
}
